import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { ImportSkipReason, ImportTemplatesResult } from 'modules/manager/ui/hooks/use-import-templates';
import React from 'react';
import type { MessageDescriptor } from 'shared/i18n';
import { UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

type RejectedError = Extract<ImportTemplatesResult, { status: 'rejected' }>['error'];

const REJECTED_TEXT: Record<RejectedError, MessageDescriptor> = {
  'not-json': msg`Это не файл шаблонов ImGame`,
  'wrong-format': msg`Это не файл шаблонов ImGame`,
  'newer-version': msg`Файл сделан в более новой версии админки — обновите страницу`,
  empty: msg`В файле нет ни одного шаблона`,
  'read-failed': msg`Не удалось прочитать файл`,
};

const SKIP_TEXT: Record<ImportSkipReason, MessageDescriptor> = {
  'not-object': msg`запись в файле повреждена`,
  'no-name': msg`у шаблона нет названия`,
  'no-objects': msg`нет содержимого холста`,
  'bad-size': msg`неверный размер холста`,
  'unknown-template-type': msg`такой тип шаблона эта версия админки не знает`,
  'name-taken': msg`уже есть общий шаблон с таким именем`,
  'no-free-name': msg`не удалось подобрать свободное имя`,
  'request-failed': msg`не удалось сохранить — ошибка сети или сервера`,
};

interface ImportTemplatesResultModalProps {
  result: ImportTemplatesResult;
  onClose: () => void;
}

/** Outcome of a template file import: what was created, under which name, and what was skipped. */
function ImportTemplatesResultModal({ result, onClose }: ImportTemplatesResultModalProps) {
  const { t } = useLingui();

  if (result.status === 'rejected') {
    return (
      <Modal show onClose={onClose} size="md">
        <Modal.Header title={t`Файл не загружен`} subtitle={result.fileName} />
        <Modal.Body>
          <UDText variant="body-2" className="text-CaseErrorText">
            {t(REJECTED_TEXT[result.error])}
          </UDText>
          <UDText variant="body-3" className="text-inkSecondary mt-2">
            <Trans>Ни один шаблон не создан.</Trans>
          </UDText>
        </Modal.Body>
        <Modal.Footer>
          <UDButton variant="primary" onClick={onClose}>
            <Trans>Понятно</Trans>
          </UDButton>
        </Modal.Footer>
      </Modal>
    );
  }

  const { created, overwritten, skipped, total } = result;
  const importedCount = created.length + overwritten.length;

  return (
    <Modal show onClose={onClose} size="md">
      <Modal.Header title={t`Импортировано ${importedCount} из ${total}`} subtitle={result.fileName} />
      {/* No scroll of its own: `UDModal` already scrolls past 90vh, and a nested
          `overflow-y: auto` showed a scrollbar for a single line — text overflows its
          line box by a pixel or two. */}
      <Modal.Body className="flex flex-col gap-4">
        {created.length > 0 && (
          <section>
            <UDText variant="body-2" className="text-CaseSuccessText mb-1 font-semibold">
              <Trans>Создано</Trans>
            </UDText>
            <ul className="flex flex-col gap-1" aria-label={t`Созданные шаблоны`}>
              {created.map((template) => {
                const { sourceName } = template;
                return (
                  <li key={template.id}>
                    <UDText variant="body-3" className="text-inkPrimary">
                      {template.name}
                      {template.name !== sourceName && (
                        <span className="text-inkSecondary">
                          {' — '}
                          <Trans>в файле «{sourceName}», имя было занято</Trans>
                        </span>
                      )}
                    </UDText>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {overwritten.length > 0 && (
          <section>
            <UDText variant="body-2" className="text-CasePendingText mb-1 font-semibold">
              <Trans>Перезаписано</Trans>
            </UDText>
            <ul className="flex flex-col gap-1" aria-label={t`Перезаписанные шаблоны`}>
              {overwritten.map((template) => (
                <li key={template.id}>
                  <UDText variant="body-3" className="text-inkPrimary">
                    {template.name}
                  </UDText>
                </li>
              ))}
            </ul>
          </section>
        )}
        {skipped.length > 0 && (
          <section>
            <UDText variant="body-2" className="text-CaseErrorText mb-1 font-semibold">
              <Trans>Пропущено</Trans>
            </UDText>
            <ul className="flex flex-col gap-1" aria-label={t`Пропущенные шаблоны`}>
              {skipped.map((entry) => {
                const entryNumber = entry.index + 1;
                return (
                  <li key={entry.index}>
                    <UDText variant="body-3" className="text-inkPrimary">
                      {entry.name ?? t`Шаблон №${entryNumber}`}
                      <span className="text-inkSecondary"> — {t(entry.cause ?? SKIP_TEXT[entry.reason])}</span>
                    </UDText>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </Modal.Body>
      <Modal.Footer>
        <UDButton variant="primary" onClick={onClose}>
          <Trans>Готово</Trans>
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
}

export default ImportTemplatesResultModal;
